import * as React from "react";
import { useParams } from "react-router-dom";
import { useGo } from "@refinedev/core";

import type {
  DecisionRoomClaim,
  DecisionRoomEvidenceRelation,
  DecisionRoomPacket,
} from "@aos/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Icon } from "@/components/ui/icon";
import { ErrorState } from "@/components/ui/states";
import {
  RenderState,
  type RenderStateKind,
} from "@/components/app/RenderState";
import { StatusBadge } from "@/components/app/StatusBadge";
import { PageHeader } from "@/components/layout/PageHeader";
import { Field, FieldGrid, InspectorSection, RefLink } from "@/components/inspector";
import { api, ApiError } from "@/lib/api";
import { formatDateTime, humanize } from "@/lib/format";

function useDecisionRoomPacket(decisionId: string) {
  const [data, setData] = React.useState<DecisionRoomPacket | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<unknown>(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  const reload = React.useCallback(() => setReloadKey((value) => value + 1), []);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<DecisionRoomPacket>(`/decisions/${encodeURIComponent(decisionId)}/room`)
      .then((packet) => {
        if (!cancelled) setData(packet);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [decisionId, reloadKey]);

  return { data, loading, error, reload };
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <Card>
      <CardHeader className="p-3 pb-1">
        <CardTitle className="text-xs font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-0">
        <p className="font-mono text-sm tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}

function ClaimCard({ claim }: { claim: DecisionRoomClaim }) {
  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base [overflow-wrap:anywhere]">{claim.title}</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground [overflow-wrap:anywhere]">
              {claim.statement}
            </p>
          </div>
          <StatusBadge status={claim.status} />
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
          <Badge variant="outline">{humanize(claim.claim_type)}</Badge>
          {claim.confidence_score === null ? null : (
            <Badge variant="secondary">Confidence {claim.confidence_score}</Badge>
          )}
          <span className="font-mono">{claim.id}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function RelationCard({ relation }: { relation: DecisionRoomEvidenceRelation }) {
  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base [overflow-wrap:anywhere]">
              {relation.evidence?.title ?? relation.source_id}
            </CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              {humanize(relation.relation_type)} claim{" "}
              <span className="font-mono text-xs">{relation.claim_id}</span>
            </p>
          </div>
          <StatusBadge status={relation.status} />
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <FieldGrid>
          <Field label="Evidence">
            {relation.evidence_id ? (
              <RefLink resource="evidence" id={relation.evidence_id} />
            ) : (
              <span className="font-mono text-xs">{relation.source_id}</span>
            )}
          </Field>
          <Field label="Source">
            <span className="font-mono text-xs">
              {relation.source_type}:{relation.source_id}
            </span>
          </Field>
          <Field label="Weight">{relation.weight ?? "—"}</Field>
          <Field label="Confidence">{relation.confidence_score ?? "—"}</Field>
        </FieldGrid>
      </CardContent>
    </Card>
  );
}

function DecisionRoomContent({
  packet,
  reload,
}: {
  packet: DecisionRoomPacket;
  reload: () => void;
}) {
  const readiness = packet.readiness;
  // The packet's `warnings` are non-blocking projection notes (e.g. a partial
  // trace, a collapsed/aggregated section). Surface them as an overflow banner
  // above the still-rendered business state, per the eight-state contract.
  const warnings = packet.warnings;
  return (
    <RenderState
      state={warnings.length > 0 ? "overflow" : "normal"}
      overflowMessage={
        warnings.length > 0
          ? warnings.join(" · ")
          : undefined
      }
    >
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        <Metric label="Active claims" value={readiness.active_claims} />
        <Metric label="Accepted" value={readiness.accepted_relations} />
        <Metric label="Candidates" value={readiness.candidate_relations} />
        <Metric label="Needs review" value={readiness.needs_review_relations} />
        <Metric label="Missing evidence" value={readiness.missing_evidence} />
        <Metric label="Contradictions" value={readiness.contradictions} />
      </div>

      <InspectorSection
        title="Readiness"
        aside={
          <span className="text-xs text-muted-foreground">
            Generated {formatDateTime(packet.generated_at)}
          </span>
        }
      >
        <FieldGrid>
          <Field label="Decision">
            <RefLink resource="decisions" id={packet.decision.id} label={packet.decision.title} />
          </Field>
          <Field label="State">
            <StatusBadge status={readiness.display_state} />
          </Field>
          <Field label="Ready">{readiness.ready ? "Yes" : "No"}</Field>
          <Field label="Artifact">
            <RefLink
              resource="artifacts"
              id={packet.primary_artifact?.id}
              label={packet.primary_artifact?.title}
            />
          </Field>
          <Field label="Project">
            <RefLink resource="projects" id={packet.decision.project_id} />
          </Field>
          <Field label="Status">
            <StatusBadge status={packet.decision.status} />
          </Field>
        </FieldGrid>
        {readiness.blockers.length > 0 ? (
          <div className="mt-4 rounded-md border border-border bg-muted/30 px-4 py-3">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium">
              <Icon name="decisions" aria-hidden />
              Blockers
            </div>
            <ul className="list-inside list-disc text-sm text-muted-foreground">
              {readiness.blockers.map((blocker) => (
                <li key={blocker}>{blocker}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </InspectorSection>

      <InspectorSection
        title="Claims"
        aside={
          <Button variant="outline" size="sm" onClick={reload}>
            <Icon name="reopen" data-icon="inline-start" aria-hidden />
            Refresh
          </Button>
        }
      >
        {packet.claims.length === 0 ? (
          <RenderState
            state="empty"
            empty={{
              icon: "evidence",
              title: "No claims attached to this decision.",
              hint: "Decision Room readiness starts when claims are attached to the governed artifact.",
            }}
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            {packet.claims.map((claim) => (
              <ClaimCard key={claim.id} claim={claim} />
            ))}
          </div>
        )}
      </InspectorSection>

      <InspectorSection title="Evidence Relations">
        {packet.evidence_relations.length === 0 ? (
          <RenderState
            state="empty"
            empty={{
              icon: "evidence",
              title: "No evidence relations yet.",
              hint: "Only accepted evidence relations count toward readiness.",
            }}
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            {packet.evidence_relations.map((relation) => (
              <RelationCard key={relation.id} relation={relation} />
            ))}
          </div>
        )}
      </InspectorSection>

      <InspectorSection title="Deliverables">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {packet.deliverables.map((artifact) => (
            <Card key={artifact.id}>
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-base [overflow-wrap:anywhere]">
                  {artifact.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge status={artifact.status} />
                  <Badge variant="outline">{humanize(artifact.artifact_type)}</Badge>
                  <RefLink resource="artifacts" id={artifact.id} label="Open artifact" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </InspectorSection>

      <InspectorSection title="Trace">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Metric label="Events" value={packet.trace.events.length} />
          <Metric label="Rule runs" value={packet.trace.rule_runs.length} />
          <Metric label="Agent runs" value={packet.trace.agent_runs.length} />
        </div>
      </InspectorSection>
    </div>
    </RenderState>
  );
}

export function DecisionRoomRoute() {
  const { id } = useParams<{ id: string }>();

  if (!id) return <ErrorState message="Missing decision id." />;

  return <DecisionRoomPage decisionId={id} />;
}

/** True when the packet describes a Decision Room with nothing in it yet. */
function isEmptyRoom(packet: DecisionRoomPacket): boolean {
  return (
    packet.claims.length === 0 &&
    packet.evidence_relations.length === 0 &&
    packet.deliverables.length === 0
  );
}

function DecisionRoomPage({ decisionId }: { decisionId: string }) {
  const go = useGo();
  const { data, loading, error, reload } = useDecisionRoomPacket(decisionId);

  // Route the screen body through the shared eight-state contract (S13). A 403
  // is a `forbidden` downgrade (with the approval path); any other failure is a
  // recoverable `error`; an authorized-but-empty room is `empty`; a populated
  // room is `normal` (its non-blocking `warnings` become an overflow banner,
  // handled inside DecisionRoomContent).
  const forbidden = error instanceof ApiError && error.status === 403;
  const state: RenderStateKind = loading
    ? "loading"
    : forbidden
      ? "forbidden"
      : error
        ? "error"
        : data && isEmptyRoom(data)
          ? "empty"
          : "normal";

  return (
    <div className="max-w-6xl">
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Decision Room"
          subtitle="Evidence-backed decision surface: readiness, claims, canonical evidence relations, deliverables, and trace."
          icon="decisions"
          actions={
            <Button variant="outline" onClick={() => go({ to: `/decisions/${decisionId}` })}>
              <Icon name="decisions" data-icon="inline-start" aria-hidden />
              Decision
            </Button>
          }
        />
        <RenderState
          state={state}
          loadingRows={5}
          error={error}
          onRetry={reload}
          empty={{
            icon: "decisions",
            title: "This Decision Room is empty.",
            hint: "Attach claims and evidence to the governed artifact to start readiness.",
          }}
          forbiddenReason="You do not have access to this Decision Room."
          forbiddenApprovalPath="the decision owner or an administrator"
        >
          {data && !isEmptyRoom(data) ? (
            <DecisionRoomContent packet={data} reload={reload} />
          ) : null}
        </RenderState>
      </div>
    </div>
  );
}

export default DecisionRoomRoute;
